<script setup lang="ts">
/**
 * @name DateControl
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE QUE PERMITE CONTROLAR LA FECHA DE UN CERTIFICADO
 *
 * responsabilidades:
 * R1: PERMITE MOSTRAR UN INPUT DE FECHA CON FORMATO ESPAÑOL Y VERIFICAR SI LA FECHA ES VÁLIDA DEPENDIENDO DE LA FUNCIÓN VERIFICARFECHA
 * @changelog 1.0.0 - 2025-06-17 - @Jorel254 - Versión inicial del componente
 */

import { ref, watch } from 'vue'
import { VaDateInput } from 'vuestic-ui'
const props = defineProps<{
	value: any
	disabled: boolean
	defaultValue: boolean
	label: string
	rules?: any[]
	verificarFecha?: (date: Date) => boolean
}>()
const emit = defineEmits(['update-Date'])

const finalValue = ref<Date>(new Date())

// Función para parsear fechas en formato español (DD/MM/YYYY)
const parseSpanishDate = (dateString: string): Date => {
	// Patrones para diferentes formatos de fecha española
	const patterns = [
		/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // DD/MM/YYYY
		/^(\d{1,2})-(\d{1,2})-(\d{4})$/, // DD-MM-YYYY
		/^(\d{4})-(\d{2})-(\d{2})$/ // YYYY-MM-DD (formato ISO)
	]

	for (const pattern of patterns) {
		const match = dateString.match(pattern)
		if (match) {
			let day, month, year

			if (pattern.source.includes('YYYY')) {
				// Formato YYYY-MM-DD
				year = parseInt(match[1])
				month = parseInt(match[2]) - 1
				day = parseInt(match[3])
			} else {
				// Formatos DD/MM/YYYY o DD-MM-YYYY
				day = parseInt(match[1])
				month = parseInt(match[2]) - 1
				year = parseInt(match[3])
			}

			const date = new Date(year, month, day)

			// Verificar que la fecha sea válida
			if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
				return date
			}
		}
	}

	// Si no se puede parsear, devolver la fecha actual
	return new Date()
}

// Función para obtener el valor inicial
const getInitialValue = () => {
	if (props.value) {
		if (typeof props.value === 'string') {
			// YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss → fecha local (sin desplazamiento UTC)
			const match = props.value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/)
			if (match) {
				const year = match[1]
				const month = match[2]
				const day = match[3]
				return new Date(Number(year), Number(month) - 1, Number(day))
			}
			if (props.value === 'Invalid date') {
				return new Date()
			}
			return new Date(props.value)
		} else if (typeof props.value === 'number') {
			return new Date(props.value)
		} else {
			return props.value
		}
	} else if (props.defaultValue) {
		return null
	}
	return new Date()
}

// Inicializar el valor
finalValue.value = getInitialValue()

// Watch para la prop value - esto es lo que faltaba
watch(
	() => props.value,
	newValue => {
		if (newValue) {
			finalValue.value = getInitialValue()
		}
	},
	{ immediate: true }
)

watch(finalValue, newValue => {
	let date = new Date(newValue).getTime()
	emit('update-Date', date)
})
</script>
<template>
	<VaDateInput
		v-model="finalValue"
		:label="props.label"
		:format-date="date => date.toLocaleDateString('es-ES')"
		:weekday-names="['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']"
		:month-names="['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']"
		:disabled="props.disabled"
		placeholder="DD/MM/YYYY"
		:rules="rules"
		manual-input
		:parse-date="parseSpanishDate"
		:allowed-days="verificarFecha" />
</template>
